/**
 * Hashing utilities for deterministic codes and compatibility.
 *
 * - hashStringTo32Bit: Java's String.hashCode() (32-bit signed). Use for
 *   backwards compatibility (e.g. legacy referral codes).
 * - hashStringTo32BitSha256: First 4 bytes of SHA-256 as uint32. Use for new
 *   deterministic codes where compatibility with legacy hashes is not needed.
 * - hashStringToHex: SHA-256 as hex, truncated to a caller-chosen width. Use
 *   when the hash is an identity (a document id, a lookup key) rather than a
 *   bucket, so collisions must stay negligible.
 */
import {sha256} from '@noble/hashes/sha2.js';

/** Default width for {@link hashStringToHex}: 32 hex characters = 128 bits. */
const DEFAULT_HASH_HEX_LENGTH = 32;

/**
 * Java's String.hashCode(): 32-bit signed integer, deterministic.
 * Matches react-hash-string's hashString() and Java's String.hashCode().
 * Use for backwards compatibility with react-hash-string hashString() function.
 *
 * @deprecated Use hashStringTo32BitSha256 instead.
 */
export function hashStringTo32Bit(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return h;
}

/**
 * Deterministic 32-bit unsigned integer from string via SHA-256.
 * Uses first 4 bytes of SHA-256 (big-endian). Use for new courses/features
 * where legacy hash compatibility is not required.
 */
export function hashStringTo32BitSha256(str: string): number {
  const bytes = new TextEncoder().encode(str);
  const digest = sha256(bytes);
  return (
    ((digest[0] << 24) | (digest[1] << 16) | (digest[2] << 8) | digest[3]) >>> 0
  );
}

/**
 * Deterministic hex digest of a string via SHA-256, truncated to `hexLength`
 * characters.
 *
 * Use this — not {@link hashStringTo32BitSha256} — whenever the result
 * identifies something. The 32-bit helper keeps 4 of SHA-256's 32 bytes, which
 * is fine for choosing a bucket (collisions are expected and harmless) and
 * unsafe for an id: at 32 bits, 50,000 distinct inputs collide with roughly
 * 25% probability, and a colliding id silently aliases two different things.
 *
 * Each hex character carries 4 bits, so `hexLength` of 32 gives a 128-bit id.
 *
 * @param str - Input to hash. Callers normalize first when two spellings of
 *   the same thing must produce the same id.
 * @param hexLength - Number of hex characters to keep, from 1 to 64 (SHA-256
 *   is 64 hex characters). Defaults to 32, a 128-bit id.
 * @return Lowercase hex string of exactly `hexLength` characters.
 * @throws `RangeError` when `hexLength` is not an integer in [1, 64].
 */
export function hashStringToHex(
  str: string,
  hexLength: number = DEFAULT_HASH_HEX_LENGTH,
): string {
  if (!Number.isInteger(hexLength) || hexLength < 1 || hexLength > 64) {
    throw new RangeError(
      `hashStringToHex: hexLength must be an integer in [1, 64], got ${String(hexLength)}`,
    );
  }
  const digest = sha256(new TextEncoder().encode(str));
  let hex = '';
  for (const byte of digest) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex.slice(0, hexLength);
}
